import type { PoliceDepartmentEnrichmentStatus } from '@/services/api/generated/policeDepartmentsService';
import type {
  OfficerDetail,
  PoliceReportAttestationStatus,
  ReadPoliceReport,
} from '@/services/api/generated/policeReportsService';

// Re-export types
export type {
  OfficerDetail,
  PoliceDepartmentEnrichmentStatus,
  PoliceReportAttestationStatus,
  ReadPoliceReport,
};

// Status config types
export interface PoliceReportStatusConfig {
  label: string;
  shortLabel: string;
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
  description: string;
  priority: number;
  icon: string;
  isFinal: boolean;
  badgeColor: {
    bg: string;
    text: string;
    border: string;
  };
  cardColor: {
    from: string;
    to: string;
    accent: string;
  };
}

export interface EnrichmentStatusConfig {
  label: string;
  shortLabel: string;
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
  description: string;
  loadingText?: string;
  successText?: string;
  actionable: boolean;
  actionText?: string;
  badgeColor: {
    bg: string;
    text: string;
    border: string;
  };
  icon: string;
}

export const POLICE_REPORT_STATUS_CONFIG: Record<
  PoliceReportAttestationStatus,
  PoliceReportStatusConfig
> = {
  inferred: {
    label: 'System Inferred',
    shortLabel: 'Inferred',
    variant: 'secondary',
    description:
      'System-generated lead based on incident location or circumstantial evidence',
    priority: 0,
    icon: 'information-circle',
    isFinal: false,
    badgeColor: {
      bg: 'bg-gray-50',
      text: 'text-gray-600',
      border: 'border-gray-200',
    },
    cardColor: {
      from: 'from-gray-50',
      to: 'to-gray-100',
      accent: 'border-gray-300',
    },
  },
  unclear: {
    label: 'Status Unknown',
    shortLabel: 'Unknown',
    variant: 'secondary',
    description:
      "Haven't determined if this department has a report for this incident",
    priority: 1,
    icon: 'question-mark-circle',
    isFinal: false,
    badgeColor: {
      bg: 'bg-gray-100',
      text: 'text-gray-700',
      border: 'border-gray-200',
    },
    cardColor: {
      from: 'from-gray-50',
      to: 'to-gray-100',
      accent: 'border-gray-300',
    },
  },
  attested_unverified: {
    label: 'Confirmed, Unverified',
    shortLabel: 'Unverified',
    variant: 'secondary',
    description:
      "Believe this department has the report, but haven't confirmed with them",
    priority: 2,
    icon: 'check-circle',
    isFinal: false,
    badgeColor: {
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
    },
    cardColor: {
      from: 'from-blue-50',
      to: 'to-indigo-50',
      accent: 'border-blue-300',
    },
  },
  rejected_verified: {
    label: 'Confirmed - No Report',
    shortLabel: 'No Report',
    variant: 'secondary',
    description:
      "Department confirmed they don't have a report for this incident",
    priority: 4,
    icon: 'check-circle',
    isFinal: true,
    badgeColor: {
      bg: 'bg-gray-50',
      text: 'text-gray-700',
      border: 'border-gray-200',
    },
    cardColor: {
      from: 'from-gray-50',
      to: 'to-slate-50',
      accent: 'border-gray-300',
    },
  },
  attested_verified: {
    label: 'Confirmed, Verified',
    shortLabel: 'Verified',
    variant: 'default',
    description: 'Department confirmed they have the report for this incident',
    priority: 3,
    icon: 'check-circle',
    isFinal: false,
    badgeColor: {
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
    },
    cardColor: {
      from: 'from-blue-50',
      to: 'to-indigo-50',
      accent: 'border-blue-300',
    },
  },
  verified_received: {
    label: 'Received',
    shortLabel: 'Received',
    variant: 'default',
    description: 'We have obtained the report from this department',
    priority: 5,
    icon: 'document-check',
    isFinal: true,
    badgeColor: {
      bg: 'bg-green-50',
      text: 'text-green-700',
      border: 'border-green-200',
    },
    cardColor: {
      from: 'from-green-50',
      to: 'to-emerald-50',
      accent: 'border-green-300',
    },
  },
};

export const ENRICHMENT_STATUS_CONFIG: Record<
  PoliceDepartmentEnrichmentStatus,
  EnrichmentStatusConfig
> = {
  not_enriched: {
    label: 'Basic Info Only',
    shortLabel: 'Basic',
    variant: 'secondary',
    description: 'We have basic department info but no contact details yet',
    loadingText: 'Basic information only',
    actionable: true,
    actionText: 'Find Contact Info',
    badgeColor: {
      bg: 'bg-gray-100',
      text: 'text-gray-600',
      border: 'border-gray-300',
    },
    icon: 'information-circle',
  },
  enrichment_in_progress: {
    label: 'Gathering Information...',
    shortLabel: 'Enriching...',
    variant: 'outline',
    description: 'Currently finding contact information and procedures',
    loadingText: 'Finding contact details...',
    actionable: false,
    badgeColor: {
      bg: 'bg-blue-50',
      text: 'text-blue-600',
      border: 'border-blue-200',
    },
    icon: 'arrow-path',
  },
  enrichment_failed: {
    label: 'Information Unavailable',
    shortLabel: 'Failed',
    variant: 'destructive',
    description: 'Unable to find additional contact information',
    loadingText: 'Information unavailable',
    actionable: true,
    actionText: 'Try Again',
    badgeColor: {
      bg: 'bg-red-50',
      text: 'text-red-600',
      border: 'border-red-200',
    },
    icon: 'exclamation-triangle',
  },
  enrichment_succeeded: {
    label: 'Enriched',
    shortLabel: 'Enriched',
    variant: 'default',
    description: 'Contact information sourced from official sources online',
    successText: 'Complete information available',
    actionable: false,
    badgeColor: {
      bg: 'bg-green-50',
      text: 'text-green-600',
      border: 'border-green-200',
    },
    icon: 'check-circle',
  },
};

export const getPoliceReportStatusConfig = (
  status: PoliceReportAttestationStatus | null | undefined,
): PoliceReportStatusConfig => {
  if (!status) {
    return POLICE_REPORT_STATUS_CONFIG.unclear;
  }

  const config = POLICE_REPORT_STATUS_CONFIG[status];
  if (!config) {
    return POLICE_REPORT_STATUS_CONFIG.unclear;
  }

  return config;
};

export const getEnrichmentStatusConfig = (
  status: PoliceDepartmentEnrichmentStatus | null | undefined,
): EnrichmentStatusConfig => {
  if (!status) {
    return ENRICHMENT_STATUS_CONFIG.not_enriched;
  }

  return (
    ENRICHMENT_STATUS_CONFIG[status] || ENRICHMENT_STATUS_CONFIG.not_enriched
  );
};

export const getEnrichmentDisplayInfo = getEnrichmentStatusConfig;

export const getPoliceEmptyStateMessage = (): {
  title: string;
  description: string;
} => {
  return {
    title: 'No Police Reports',
    description:
      'There are no police reports associated with this matter yet. Click below to add one.',
  };
};

export const hasPoliceReportEvidence = (
  report: ReadPoliceReport | null | undefined,
): boolean => {
  if (!report) return false;

  return !!(
    report.report_identifier ||
    report.reported_incident_date ||
    report.reported_location ||
    (report.involved_officers && report.involved_officers.length > 0)
  );
};

export const getQuickStatusActions = (
  currentStatus: PoliceReportAttestationStatus | null | undefined,
): Array<{
  status: PoliceReportAttestationStatus;
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
}> => {
  const current = currentStatus || 'unclear';

  switch (current) {
    case 'unclear':
      return [
        {
          status: 'attested_unverified',
          label: 'Report Likely Exists',
          variant: 'outline',
        },
        {
          status: 'rejected_verified',
          label: 'No Report Exists',
          variant: 'destructive',
        },
      ];

    case 'attested_unverified':
      return [
        {
          status: 'attested_verified',
          label: 'Confirmed by Department',
          variant: 'default',
        },
        {
          status: 'rejected_verified',
          label: 'Actually No Report',
          variant: 'destructive',
        },
      ];

    case 'attested_verified':
      return [
        {
          status: 'verified_received',
          label: 'Mark as Received',
          variant: 'default',
        },
      ];

    default:
      return [];
  }
};

export const getStatusOptions = (): Array<{
  value: PoliceReportAttestationStatus;
  label: string;
  description: string;
}> => {
  return [
    {
      value: 'unclear',
      label: 'Status Unknown',
      description: 'Department mentioned but unclear if they have a report',
    },
    {
      value: 'inferred',
      label: 'System Inferred',
      description: 'System guess based on location or jurisdiction',
    },
    {
      value: 'attested_unverified',
      label: 'Confirmed, Unverified',
      description: 'Client or secondary source confirmed involvement',
    },
    {
      value: 'attested_verified',
      label: 'Confirmed, Verified',
      description: 'Department confirmed they have the report',
    },
    {
      value: 'rejected_verified',
      label: 'Confirmed - No Report',
      description: 'Department confirmed they do not have a report',
    },
    {
      value: 'verified_received',
      label: 'Received',
      description: 'Report has been received and added to case files',
    },
  ];
};
